import { useEffect, useMemo } from 'react';

import { isNotDefined } from '@enterprise/core';

import type { SseProviderProps } from './types/sse-provider.props';

import { SseContext } from './sse.context';
import { ReconnectReason } from './types/reconnect-reason.enum';
import { getSseRuntimeStore } from './utilities/get-sse-runtime-store';

/**
 * Provides a context for managing a Server-Sent Events (SSE) connection within a React application.
 *
 * This provider initializes and manages the SSE runtime store based on the given configuration.
 * It connects to the SSE endpoint when mounted and optionally passes global messages to a callback.
 * If SSE is not enabled or the URI is not defined, it simply renders its children without providing the context.
 *
 * @param {SseProviderProps} props - The properties for configuring the SSE provider.
 * @param {React.ReactNode} props.children - The child components to render within the provider.
 * @param {boolean} [props.enabled=true] - Whether SSE is enabled.
 * @param {Record<string, string>} [props.headers={}] - Optional headers to include in the SSE connection.
 * @param {(event: MessageEvent) => void} [props.onGlobalMessage] - Optional callback for handling global SSE messages.
 * @param {string} props.uri - The URI of the SSE endpoint.
 *
 * @returns {JSX.Element} The context provider wrapping the children, or just the children if SSE is not enabled.
 */
export const SseProvider = ({ children, enabled = true, headers = {}, onGlobalMessage, uri }: SseProviderProps) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: store should only be created once
  const store = useMemo(() => {
    if (!enabled || isNotDefined(uri)) {
      return null;
    }

    return getSseRuntimeStore(uri, headers, onGlobalMessage);
  }, []);

  useEffect(() => {
    if (isNotDefined(store) || !enabled) {
      return;
    }

    store.connect(ReconnectReason.ProviderMounted);
  }, [store, enabled]);

  if (isNotDefined(store)) {
    return <>{children}</>;
  }

  return <SseContext.Provider value={store}>{children}</SseContext.Provider>;
};
